// src/components/payments/HostedCardFields.tsx
//
// The card step, rendered as three empty boxes.
//
// ============================================================================
// CRM RENDERS NO CARD INPUT
// ============================================================================
//
// Each <div> below is a mount point. The gateway's script replaces it with an
// iframe from the gateway's own origin, and the customer types into THAT. This
// component has no <input>, no onChange, no ref reading a value, and no form
// submission carrying one. There is nothing here for a CRM error reporter,
// session recorder or autofill heuristic to capture, because the values never
// exist in this document.
//
// The only thing that comes back is a single-use token, which goes straight up
// to the dialog and from there directly to Payments.
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useHostedFieldFocus, HOSTED_FIELD_ATTR } from './useHostedFieldFocus';
import {
  CARD_FIELD_SELECTORS,
  mountCardFields,
  type TokenizationOutcome,
  type TokenizerHandle,
} from '../../lib/collectJsTokenizer';

/** Selectors are ids; the mount points must carry exactly those ids. */
const id = (selector: string) => selector.replace(/^#/, '');

export interface HostedCardFieldsProps {
  /** Public browser material for this connection, as supplied by Payments. */
  publicKey: string;
  onResult: (outcome: TokenizationOutcome) => void;
  onCancel: () => void;
}

/**
 * `mounting` now means "waiting for the GATEWAY to confirm its fields exist",
 * not "we rendered our boxes". The distinction is the whole point: our mount
 * elements render either way, which is how an unusable form was presented as
 * ready.
 */
type Status = 'mounting' | 'ready' | 'submitting' | 'unavailable';

/**
 * The ONE visible boundary of a card field.
 *
 * The gateway mounts its iframe inside this div and its input is transparent
 * (see `HOSTED_FIELD_CSS`), so this class is the entire look of the field:
 * border, radius, background, padding -- and the focus ring.
 *
 * FOCUS IS NOT `:focus-within`. That was tried and does not work: `:focus`
 * does not cross a document boundary, so an ancestor of a focused iframe never
 * matches, same-origin or cross-origin. See `useHostedFieldFocus`, which reads
 * `document.activeElement` instead and drives `FIELD_SHELL_FOCUSED` below.
 *
 * `levelup-hosted-field` is not decoration -- index.css uses it to size the
 * iframe ELEMENT (not its contents) so the field's height does not depend on
 * whatever default the gateway picks.
 */
const FIELD_SHELL =
  'levelup-hosted-field h-9 rounded-md border bg-white px-2 transition-colors';
/** Resting border. */
const FIELD_SHELL_RESTING = 'border-slate-300';
/** Focused: darker border plus a ring, applied from JS, not from CSS. */
const FIELD_SHELL_FOCUSED = 'border-slate-900 ring-2 ring-slate-900/15';

/** Shell class for one field, focused or not. */
function shellClass(field: string, focused: string | null): string {
  return `${FIELD_SHELL} ${focused === field ? FIELD_SHELL_FOCUSED : FIELD_SHELL_RESTING}`;
}


export function HostedCardFields({
  publicKey,
  onResult,
  onCancel,
}: HostedCardFieldsProps): React.ReactElement {
  const [status, setStatus] = useState<Status>('mounting');
  const [complete, setComplete] = useState(false);
  const handle = useRef<TokenizerHandle | null>(null);

  /**
   * THE MOUNT EFFECT MUST NOT DEPEND ON THIS CALLBACK.
   *
   * Re-running it calls `CollectJS.configure()` a second time, and that tears
   * the gateway's iframes down and builds new ones: focus is lost, anything
   * already typed is gone, and further keystrokes land nowhere. Measured
   * against the real library -- 4 characters before, 0 after, a different
   * iframe src.
   *
   * `onResult` is an inline arrow at the caller, so its identity changes on
   * EVERY parent render. And a parent render is not rare here: focusing a
   * cross-origin iframe blurs the top window, and RTK Query's
   * `refetchOnFocus` refetches when it returns, re-rendering the panel above
   * this component. Clicking the card field was therefore enough to destroy
   * the card field.
   *
   * The ref keeps the LATEST callback without making its identity a mount
   * input. Nothing is frozen: the result path below dereferences it at the
   * moment the gateway answers, so a parent that swaps the callback (with a
   * newer provisioning authority in its closure, say) gets the new one.
   */
  const onResultRef = useRef(onResult);
  useEffect(() => {
    onResultRef.current = onResult;
  }, [onResult]);

  useEffect(() => {
    let live = true;

    void (async () => {
      const mounted = await mountCardFields({
        tokenizationKey: publicKey,
        onFieldValidity: (allValid) => {
          if (live) setComplete(allValid);
        },
      });

      if (!live) return;
      if (!mounted) {
        setStatus('unavailable');
        onResultRef.current({
          ok: false,
          reason: 'unavailable',
          detail: 'the secure card form could not be loaded',
        });
        return;
      }

      handle.current = mounted;
      setStatus('ready');

      const outcome = await mounted.result;
      if (live) onResultRef.current(outcome);
    })();

    return () => {
      live = false;
    };
    // publicKey ONLY. It is the sole input whose change means a different
    // gateway configuration and therefore genuinely needs new fields.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [publicKey]);

  const submit = useCallback(() => {
    setStatus('submitting');
    handle.current?.submit();
  }, []);

  // Which field the browser says is focused. See `useHostedFieldFocus`.
  const focusedField = useHostedFieldFocus();

  return (
    <div data-testid="hosted-card-fields">
      <p className="text-sm text-slate-700">
        Enter your card details below. They go straight to our payment provider — we never see or
        store them.
      </p>

      {status === 'unavailable' ? (
        <p className="mt-3 text-sm text-rose-700" data-testid="card-fields-unavailable">
          The secure card form could not be loaded. Please try again shortly.
        </p>
      ) : (
        <div className="mt-3 space-y-2">
          {status === 'mounting' && (
            <p className="text-xs text-slate-500" data-testid="card-fields-preparing">
              Preparing the secure card form…
            </p>
          )}
          {/* Empty on purpose. The gateway mounts an iframe into each one. */}
          <label className="block text-xs font-medium text-slate-600" htmlFor={id(CARD_FIELD_SELECTORS.number)}>
            Card number
          </label>
          <div
            id={id(CARD_FIELD_SELECTORS.number)}
            {...{ [HOSTED_FIELD_ATTR]: 'number' }}
            className={shellClass('number', focusedField)}
          />

          <div className="flex gap-2">
            <div className="flex-1">
              <label
                className="block text-xs font-medium text-slate-600"
                htmlFor={id(CARD_FIELD_SELECTORS.expiry)}
              >
                Expiry
              </label>
              <div
                id={id(CARD_FIELD_SELECTORS.expiry)}
                {...{ [HOSTED_FIELD_ATTR]: 'expiry' }}
                className={shellClass('expiry', focusedField)}
              />
            </div>
            <div className="flex-1">
              <label
                className="block text-xs font-medium text-slate-600"
                htmlFor={id(CARD_FIELD_SELECTORS.securityCode)}
              >
                Security code
              </label>
              <div
                id={id(CARD_FIELD_SELECTORS.securityCode)}
                {...{ [HOSTED_FIELD_ATTR]: 'securityCode' }}
                className={shellClass('securityCode', focusedField)}
              />
            </div>
          </div>
        </div>
      )}

      <div className="mt-3 flex items-center gap-3">
        <button
          type="button"
          className="rounded-md bg-slate-900 px-3 py-1.5 text-xs font-medium text-white disabled:opacity-40"
          // Disabled until the gateway reports every field valid, so a
          // half-filled form cannot produce a refusal the customer reads as a
          // declined card.
          disabled={status !== 'ready' || !complete}
          onClick={submit}
          data-testid="card-submit"
        >
          {status === 'submitting' ? 'Saving…' : 'Save card'}
        </button>
        <button
          type="button"
          className="text-xs text-slate-600 underline"
          onClick={onCancel}
          data-testid="card-cancel"
        >
          Cancel
        </button>
      </div>
    </div>
  );
}
